/**
 * AI-generated content detection from C2PA manifest data.
 * Uses the claim_generator field, C2PA action digitalSourceType,
 * and assertion labels to detect AI-generated or AI-assisted content.
 * All detection is 100% client-side — no external API calls.
 */

export interface AiDetectionResult {
  /** Whether the content appears to be AI-generated */
  isAiGenerated: boolean
  /** Names of detected AI tools */
  detectedTools: string[]
  /** Human-readable source type from C2PA digitalSourceType field */
  digitalSourceType?: string
  /** Whether an AI generative training assertion is present */
  hasAiAssertion: boolean
  /** Short human-readable description of detection */
  note: string
}

/** Known AI generator patterns matched against claim_generator */
const KNOWN_AI_GENERATORS: Array<{ pattern: RegExp; name: string }> = [
  { pattern: /adobe\s*firefly/i, name: 'Adobe Firefly' },
  { pattern: /dall[\s-]?e/i, name: 'DALL-E (OpenAI)' },
  { pattern: /stable[\s-]?diffusion/i, name: 'Stable Diffusion' },
  { pattern: /midjourney/i, name: 'Midjourney' },
  { pattern: /sora[\s/]/i, name: 'Sora (OpenAI)' },
  { pattern: /runway[\s/]/i, name: 'Runway ML' },
  { pattern: /bing\s*image/i, name: 'Bing Image Creator' },
  { pattern: /google.*imagen|imagen.*google/i, name: 'Google Imagen' },
  { pattern: /amazon.*titan|titan.*image/i, name: 'Amazon Titan' },
  { pattern: /leonardo[\s./]*ai|leonardoai/i, name: 'Leonardo AI' },
  { pattern: /ideogram/i, name: 'Ideogram' },
  { pattern: /\bflux[\s/]/i, name: 'FLUX (Black Forest Labs)' },
  { pattern: /nightcafe/i, name: 'NightCafe' },
  { pattern: /canva.*generat|generat.*canva/i, name: 'Canva AI' },
  { pattern: /\bpika[\s/]/i, name: 'Pika' },
  { pattern: /\bkling[\s/]/i, name: 'Kling AI' },
  { pattern: /heygen/i, name: 'HeyGen' },
  { pattern: /\bchat[\s-]?gpt/i, name: 'ChatGPT' },
  { pattern: /copilot.*designer|designer.*copilot/i, name: 'Microsoft Copilot Designer' },
  { pattern: /\bgrok[\s/]/i, name: 'Grok (xAI)' },
  { pattern: /\bgemini[\s/]/i, name: 'Google Gemini' },
  { pattern: /typecast/i, name: 'Typecast AI' },
]

/**
 * IPTC Digital Source Type URIs that indicate AI-generated content.
 * https://cv.iptc.org/newscodes/digitalsourcetype/
 */
const AI_SOURCE_TYPE_MAP: Record<string, string> = {
  trainedAlgorithmicMedia: 'Fully AI-generated content',
  compositeWithTrainedAlgorithmicMedia: 'AI-assisted composite',
  algorithmicallyEnhanced: 'AI-enhanced content',
}

/** C2PA assertion labels that indicate AI usage */
const AI_ASSERTION_LABELS = [
  'com.adobe.generative-ai',
  'c2pa.ai_generative_training',
  'c2pa.training-mining',
  'ai_generative',
  'generative-ai',
]

/**
 * Detect AI-generated or AI-assisted content from C2PA manifest data.
 *
 * @param claimGenerator - The claim_generator string from the active manifest
 * @param assertions - The assertions array from the active manifest
 */
export function detectAiContent(
  claimGenerator?: string | null,
  assertions?: Array<{ label: string; data?: any }>
): AiDetectionResult {
  const detectedTools: string[] = []
  let digitalSourceType: string | undefined
  let hasAiAssertion = false

  // 1. Check claim_generator for known AI tools
  if (claimGenerator) {
    for (const { pattern, name } of KNOWN_AI_GENERATORS) {
      if (pattern.test(claimGenerator) && !detectedTools.includes(name)) {
        detectedTools.push(name)
      }
    }
  }

  // 2. Parse assertions for AI indicators
  if (assertions) {
    for (const assertion of assertions) {
      const label = assertion.label || ''

      // Check for AI assertion labels
      if (AI_ASSERTION_LABELS.some(ai => label.toLowerCase().includes(ai.toLowerCase()))) {
        hasAiAssertion = true
      }

      // Check digitalSourceType within c2pa.actions
      if (label === 'c2pa.actions' && assertion.data?.actions) {
        for (const action of assertion.data.actions) {
          const dst: string | undefined =
            action.digitalSourceType ||
            action['digital_source_type']

          if (dst) {
            for (const [key, label] of Object.entries(AI_SOURCE_TYPE_MAP)) {
              if (dst.includes(key) && !digitalSourceType) {
                digitalSourceType = label
              }
            }
          }

          // Also check softwareAgent for AI tools
          const agent = typeof action.softwareAgent === 'string'
            ? action.softwareAgent
            : action.softwareAgent?.name || ''
          if (agent) {
            for (const { pattern, name } of KNOWN_AI_GENERATORS) {
              if (pattern.test(agent) && !detectedTools.includes(name)) {
                detectedTools.push(name)
              }
            }
          }
        }
      }
    }
  }

  const isAiGenerated = detectedTools.length > 0 || !!digitalSourceType

  let note = ''
  if (digitalSourceType) {
    note = digitalSourceType
  } else if (detectedTools.length > 0) {
    note = `Generated with ${detectedTools.join(', ')}`
  } else if (hasAiAssertion) {
    note = 'AI training/usage data present'
  }

  return {
    isAiGenerated,
    detectedTools,
    digitalSourceType,
    hasAiAssertion,
    note,
  }
}
